import { makeStyles } from "tss-react/mui";
import { actionPrimary, actionSecondary } from "@/shared/styles/action.styles";

const useStyles = makeStyles()((theme) => {
  const { color, typography, radius, space } = theme.design;
  const eyebrow = {
    fontFamily: typography.fontFamily,
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: ".06em",
  };

  return {
    root: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      height: "100%",
      minHeight: 0,
      overflow: "hidden",
      color: color.text.primary,
      background: color.surface.canvas,
      fontFamily: typography.fontFamily,
      "& button:focus-visible, & select:focus-visible, & [tabindex]:focus-visible": {
        outline: `2px solid ${color.action.primaryHover}`,
        outlineOffset: -2,
      },
    },
    header: {
      zIndex: 1,
      flex: "none",
      padding: `${space.xl}px ${space["2xl"]}px ${space.lg + space.xs}px`,
      borderBottom: `1px solid ${color.border.default}`,
      background: color.surface.raised,
      [theme.breakpoints.down("md")]: { padding: `${space.lg + space.xs}px ${space.xl}px ${space.lg}px` },
      [theme.breakpoints.down("sm")]: { padding: `${space.md}px ${space.lg}px` },
    },
    eyebrow: {
      ...eyebrow,
      display: "flex",
      alignItems: "center",
      gap: space.md,
      color: color.text.muted,
      "& span span": { padding: `0 ${space.xs}px`, color: color.text.secondary },
      [theme.breakpoints.down("sm")]: { fontSize: 10 },
    },
    logo: { display: "block", width: 36, height: 36, objectFit: "contain" },
    titleRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: space.lg + space.xs,
      margin: `${space.md}px 0 ${space.lg + space.xs}px`,
      [theme.breakpoints.down("md")]: { margin: `${space.sm + space.xxs}px 0 ${space.lg}px` },
    },
    title: {
      color: color.text.primary,
      fontFamily: typography.fontFamily,
      fontSize: "clamp(30px, 4vw, 42px)",
      fontWeight: 900,
      lineHeight: 1.15,
      [theme.breakpoints.down("sm")]: { fontSize: 30 },
    },
    intro: {
      marginTop: space.sm,
      color: color.text.muted,
      fontFamily: typography.fontFamily,
      fontSize: 14,
      lineHeight: typography.lineHeight.default,
      [theme.breakpoints.down("sm")]: { fontSize: 12 },
    },
    closeButton: {
      ...actionSecondary(theme),
      minWidth: "auto",
      padding: `${space.sm - 1}px ${space.sm + space.xxs}px`,
      fontSize: 13,
      "& span": { paddingLeft: space.sm + 1, fontSize: 20, lineHeight: 0 },
    },
    agreement: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: space.lg,
      padding: `${space.md}px ${space.lg}px`,
      border: `1px solid ${color.border.default}`,
      borderRadius: radius.lg,
      background: color.surface.control,
      [theme.breakpoints.down("md")]: {
        alignItems: "stretch",
        flexDirection: "column",
        gap: space.sm + space.xxs,
        padding: `${space.sm + space.xxs}px ${space.md}px`,
      },
    },
    agreementMain: {
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      "& .MuiFormControlLabel-root": { margin: 0 },
      "& .MuiCheckbox-root": { padding: 5, marginRight: 6, color: color.text.muted },
      "& .MuiCheckbox-root.Mui-checked": { color: color.action.primary },
      "& .MuiFormControlLabel-label": {
        color: color.text.primary,
        fontFamily: typography.fontFamily,
        fontSize: 15,
        fontWeight: 900,
        [theme.breakpoints.down("sm")]: { fontSize: 13 },
      },
    },
    agreementNote: {
      paddingLeft: 35,
      color: color.text.muted,
      fontSize: 12,
      [theme.breakpoints.down("sm")]: { fontSize: 11 },
    },
    actions: {
      display: "flex",
      alignItems: "center",
      gap: space.sm + space.xxs,
      flex: "none",
      "& .MuiButton-contained": {
        ...actionPrimary(theme),
        padding: `${space.sm + space.xxs}px ${space.lg + space.xxs}px`,
        fontSize: 14,
        boxShadow: "none",
        [theme.breakpoints.down("md")]: { width: "100%" },
      },
      "& .MuiButton-text": {
        color: color.text.primary,
        fontFamily: typography.fontFamily,
        fontWeight: 900,
        textTransform: "none",
      },
    },
    hint: {
      marginTop: space.sm + space.xxs,
      color: color.text.muted,
      fontFamily: typography.fontFamily,
      fontSize: 13,
    },
    error: {
      marginTop: space.sm + space.xxs,
      color: color.status.danger,
      fontFamily: typography.fontFamily,
      fontSize: 13,
      lineHeight: typography.lineHeight.default,
    },
    body: {
      display: "grid",
      gridTemplateColumns: "240px minmax(0, 1fr)",
      flex: 1,
      minHeight: 0,
      [theme.breakpoints.down("md")]: { display: "flex", flexDirection: "column" },
    },
    index: {
      overflow: "auto",
      padding: `${space.xl}px 0 ${space["2xl"]}px`,
      borderRight: `1px solid ${color.border.default}`,
      background: color.surface.container,
      [theme.breakpoints.down("md")]: { display: "none" },
    },
    indexLabel: {
      ...eyebrow,
      display: "flex",
      justifyContent: "space-between",
      gap: space.sm,
      padding: `0 ${space.lg + space.xxs}px ${space.lg}px ${space.xl - space.xxs}px`,
      color: color.text.primary,
      "& span": { color: color.text.muted, fontSize: 10 },
    },
    indexItem: {
      display: "flex",
      width: "100%",
      gap: space.md,
      padding: `${space.sm + space.xxs}px ${space.lg + space.xxs}px ${space.sm + space.xxs}px ${space.xl - space.xxs}px`,
      border: 0,
      borderLeft: "3px solid transparent",
      background: "transparent",
      color: color.text.muted,
      cursor: "pointer",
      fontFamily: typography.fontFamily,
      fontSize: 13,
      fontWeight: 700,
      lineHeight: 1.35,
      textAlign: "left",
      "& > span": { flex: "none", color: color.text.secondary, fontSize: 11 },
      "&:hover": { background: color.surface.controlHover, color: color.text.primary },
    },
    indexItemActive: {
      borderLeftColor: color.action.primary,
      background: color.surface.control,
      color: color.text.primary,
      "& > span": { color: color.action.primaryHover },
    },
    readingArea: { display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0 },
    mobileIndex: {
      display: "none",
      [theme.breakpoints.down("md")]: {
        display: "flex",
        flexDirection: "column",
        gap: space.xs + 1,
        padding: `${space.md}px ${space.lg + space.xs}px`,
        borderBottom: `1px solid ${color.border.default}`,
        background: color.surface.container,
      },
      "& label": { ...eyebrow, color: color.text.muted },
      "& select": {
        width: "100%",
        padding: "7px 9px",
        border: `1px solid ${color.border.black}`,
        borderRadius: radius.md,
        background: color.surface.control,
        color: color.text.primary,
        fontFamily: typography.fontFamily,
        fontSize: 13,
        fontWeight: 700,
      },
    },
    reader: {
      flex: 1,
      minHeight: 0,
      overflow: "auto",
      scrollBehavior: "smooth",
      "@media (prefers-reduced-motion: reduce)": { scrollBehavior: "auto" },
    },
    loading: { display: "flex", justifyContent: "center", padding: 80 },
    document: {
      maxWidth: 730,
      margin: "auto",
      padding: "36px clamp(24px, 5vw, 64px) 80px",
      [theme.breakpoints.down("md")]: { padding: `${space.xl + space.xs}px ${space.xl - space.xxs}px ${space["4xl"]}px` },
    },
    cover: {
      marginBottom: 40,
      paddingBottom: 25,
      borderBottom: `1px solid ${color.border.default}`,
      "& > p": { ...eyebrow, margin: `0 0 ${space.sm + space.xxs}px`, color: color.text.muted },
      "& > p span": { color: color.text.secondary, padding: `0 ${space.xs}px` },
      "& h2": { margin: `0 0 ${space.lg}px`, color: color.text.primary, fontFamily: typography.fontFamily, fontSize: 26, fontWeight: 900, lineHeight: 1.2 },
      "& pre": { margin: 0, color: color.text.muted, fontFamily: typography.fontFamily, fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap" },
    },
    coverRule: { width: 42, height: 3, marginBottom: space.lg, borderRadius: radius.md, background: color.action.primary },
    section: { marginBottom: 42, scrollMarginTop: 24 },
    sectionHeading: {
      display: "flex",
      alignItems: "baseline",
      gap: space.md + 1,
      marginBottom: space.lg,
      "& > span": { flex: "none", color: color.action.primaryHover, fontFamily: typography.fontFamily, fontSize: 12, fontWeight: 900 },
      "& h3": { margin: 0, color: color.text.primary, fontFamily: typography.fontFamily, fontSize: "clamp(20px, 2.5vw, 24px)", fontWeight: 900, lineHeight: 1.25 },
    },
    sectionCopy: { paddingLeft: space["2xl"], [theme.breakpoints.down("md")]: { paddingLeft: 0 } },
    paragraph: {
      margin: `0 0 ${space.lg - 1}px`,
      color: color.text.primary,
      fontFamily: typography.fontFamily,
      fontSize: 15,
      fontWeight: 400,
      lineHeight: typography.lineHeight.relaxed,
      whiteSpace: "pre-wrap",
      "&:last-child": { marginBottom: 0 },
      "& strong": { color: color.text.primary, fontWeight: 900 },
      [theme.breakpoints.down("md")]: { fontSize: 14 },
    },
    definition: { paddingLeft: space.md + 1, borderLeft: `2px solid ${color.border.default}` },
    endParagraph: {
      marginTop: space["2xl"],
      paddingTop: space.lg,
      borderTop: `1px solid ${color.border.default}`,
      color: color.text.muted,
      fontSize: 11,
      fontWeight: 900,
      lineHeight: typography.lineHeight.default,
      letterSpacing: ".05em",
    },
  };
});

export default useStyles;
